import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@context/CartContext';
import { crmService } from '@services/crmService';

// Simple pulse for cart notification only (if needed later)
// const pulse = keyframes`
//   0% {
//     opacity: 0.8;
//   }
//   50% {
//     opacity: 1;
//   }
//   100% {
//     opacity: 0.8;
//   }
// `;

// Styled Components
const NavbarContainer = styled.header<{ $isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  transition: all ${({ theme }) => theme.transitions.default};
  padding: ${({ $isScrolled, theme }) => $isScrolled ? theme.spacing['2'] : theme.spacing['4']} 0;
  backdrop-filter: blur(10px);
  
  ${({ $isScrolled, theme }) => $isScrolled 
    ? css`
        background-color: ${theme.colors.background}ee;
        box-shadow: ${theme.shadows.md};
      `
    : css`
        background-color: transparent;
      `
  }
`;

const NavContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing['4']};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 ${({ theme }) => theme.spacing['3']};
  }
`;

const LogoContainer = styled.div`
  position: relative;
  z-index: 2;
`;

const Logo = styled(Link)<{ $isScrolled: boolean }>`
  display: block;
  text-decoration: none;
  
  h1 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: ${({ $isScrolled }) => $isScrolled ? '1.5rem' : '1.8rem'};
    font-weight: 600;
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    letter-spacing: -0.02em;
    transition: font-size ${({ theme }) => theme.transitions.default};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ $isScrolled }) => $isScrolled ? '1.2rem' : '1.4rem'};
    }
  }
`;

const NavLinks = styled.nav<{ $isOpen: boolean }>`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    background: ${({ theme }) => theme.colors.surface};
    transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform ${({ theme }) => theme.transitions.default};
    z-index: ${({ theme }) => theme.zIndex.modal};
    padding: 100px ${({ theme }) => theme.spacing['5']} ${({ theme }) => theme.spacing['5']};
    display: flex;
    flex-direction: column;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing['4']};
  }
`;

const NavItem = styled.li<{ $isActive?: boolean }>`
  margin: 0 ${({ theme }) => theme.spacing['3']};
  position: relative;
  
  ${({ $isActive }) => $isActive && css`
    &::after {
      content: '';
      position: absolute;
      bottom: -3px;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: var(--color-primary);
    }
  `}
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0;
    
    ${({ $isActive }) => $isActive && css`
      &::after {
        display: none;
      }
    `}
  }
`;

const NavLink = styled(Link)<{ $isActive?: boolean }>`
  color: ${({ theme, $isActive }) => $isActive ? theme.colors.primary : theme.colors.text};
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: ${({ theme, $isActive }) => $isActive ? theme.fontWeights.bold : theme.fontWeights.medium};
  transition: all ${({ theme }) => theme.transitions.quick};
  position: relative;
  padding: ${({ theme }) => `${theme.spacing['1']} ${theme.spacing['2']}`};
  display: block;
  
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transform: scaleX(0);
    transform-origin: right;
    transition: transform ${({ theme }) => theme.transitions.default};
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    
    &::before {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
  
  ${({ $isActive }) => $isActive && css`
    &::before {
      transform: scaleX(1);
    }
  `}
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.3rem;
    padding: ${({ theme }) => theme.spacing['2']} 0;
    
    ${({ $isActive }) => $isActive && css`
      color: var(--color-primary);
      
      &::before {
        height: 0;
      }
    `}
  }
`;

const CartButton = styled.button<{ $hasItems: boolean }>`
  background: none;
  border: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  cursor: pointer;
  margin-left: ${({ theme }) => theme.spacing['3']};
  z-index: 2;
  
  svg {
    width: 28px;
    height: 28px;
    fill: ${({ theme }) => theme.colors.text};
    transition: all ${({ theme }) => theme.transitions.quick};
  }
  
  &:hover {
    svg {
      fill: ${({ theme }) => theme.colors.primary};
      transform: scale(1.1);
    }
  }
  
  ${({ $hasItems, theme }) => $hasItems && css`
    &::before {
      content: '';
      position: absolute;
      top: -2px;
      right: -2px;
      width: 8px;
      height: 8px;
      background-color: ${theme.colors.secondary};
      border-radius: 50%;
    }
  `}
`;

const CartCount = styled.span<{ $count: number }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 18px;
  height: 18px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({ $count }) => $count > 0 ? 'scale(1)' : 'scale(0)'};
  transition: transform ${({ theme }) => theme.transitions.quick};
  z-index: 1;
`;

const MobileMenuButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  position: relative;
  cursor: pointer;
  z-index: 10;
  
  span {
    display: block;
    position: absolute;
    height: 3px;
    width: 100%;
    background: ${({ theme }) => theme.colors.text};
    border-radius: 3px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: ${({ theme }) => theme.transitions.default};
    
    &:nth-child(1) {
      top: ${({ $isOpen }) => $isOpen ? '18px' : '10px'};
      transform: ${({ $isOpen }) => $isOpen ? 'rotate(135deg)' : 'rotate(0)'};
    }
    
    &:nth-child(2) {
      top: 18px;
      opacity: ${({ $isOpen }) => $isOpen ? '0' : '1'};
      transform: ${({ $isOpen }) => $isOpen ? 'translateX(20px)' : 'translateX(0)'};
    }
    
    &:nth-child(3) {
      top: ${({ $isOpen }) => $isOpen ? '18px' : '26px'};
      transform: ${({ $isOpen }) => $isOpen ? 'rotate(-135deg)' : 'rotate(0)'};
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  z-index: 5;
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

// Props interface
interface NavbarProps {
  toggleCart: () => void;
}

// Navbar component
const Navbar: React.FC<NavbarProps> = ({ toggleCart }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount } = useCart();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Listen for scroll events
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Close mobile menu when location changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Detect admin auth
  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('crm_token');
      if (!token) {
        setIsAdmin(false);
        return;
      }
      try {
        const resp = await crmService.getCurrentUser();
        if (resp.user && resp.user.legacy_token !== true) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [location.pathname]);
  
  // Check if link is active
  const isLinkActive = (path: string) => {
    return location.pathname === path;
  };
  
  return (
    <NavbarContainer $isScrolled={isScrolled}>
      <NavContent>
        <LogoContainer>
          <Logo to="/" $isScrolled={isScrolled}>
            <h1>Blagman Vintage Plus</h1>
          </Logo>
        </LogoContainer>
        
        <MobileMenuButton
          $isOpen={isMobileMenuOpen}
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuButton>
        
        <AnimatePresence>
          {isMobileMenuOpen && (
            <MobileMenuOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
        </AnimatePresence>
        
        <NavLinks $isOpen={isMobileMenuOpen}>
          <NavList>
            <NavItem $isActive={isLinkActive('/')}>
              <NavLink to="/" $isActive={isLinkActive('/')}>
                Home
              </NavLink>
            </NavItem>
            <NavItem $isActive={isLinkActive('/catalog')}>
              <NavLink to="/catalog" $isActive={isLinkActive('/catalog')}>
                Catalog
              </NavLink>
            </NavItem>
            <NavItem $isActive={isLinkActive('/about')}>
              <NavLink to="/about" $isActive={isLinkActive('/about')}>
                Legacy
              </NavLink>
            </NavItem>
            <NavItem $isActive={isLinkActive('/mad-magazine') || isLinkActive('/forbidden-elvis')}>
              <NavLink to="/mad-magazine" $isActive={isLinkActive('/mad-magazine') || isLinkActive('/forbidden-elvis')}>
                Projects
              </NavLink>
            </NavItem>
            <NavItem $isActive={isLinkActive('/licensing')}>
              <NavLink to="/licensing" $isActive={isLinkActive('/licensing')}>
                Licensing
              </NavLink>
            </NavItem>
            <NavItem $isActive={isLinkActive('/contact')}>
              <NavLink to="/contact" $isActive={isLinkActive('/contact')}>
                Contact
              </NavLink>
            </NavItem>
            {isAdmin && (
              <>
                <NavItem $isActive={isLinkActive('/admin/tracks')}>
                  <NavLink to="/admin/tracks" $isActive={isLinkActive('/admin/tracks')}>
                    Tracks CRM
                  </NavLink>
                </NavItem>
                <NavItem $isActive={isLinkActive('/admin/account')}>
                  <NavLink to="/admin/account" $isActive={isLinkActive('/admin/account')}>
                    Account
                  </NavLink>
                </NavItem>
              </>
            )}
          </NavList>
        </NavLinks>
        
        <CartButton 
          $hasItems={cartCount > 0}
          onClick={toggleCart}
          aria-label="View cart"
        >
          <svg viewBox="0 0 24 24">
            <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
          <CartCount $count={cartCount}>{cartCount}</CartCount>
        </CartButton>
      </NavContent>
    </NavbarContainer>
  );
};

export default Navbar;
